import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, NavBar, Skeleton, Toast, Dialog} from 'antd-mobile';
import { useApolloClient } from '@apollo/client';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import EmptyState from "../../../components/EmptyState";
import {t} from "../../../i18n";
import {useTmsTaskWaypoint, useTmsTaskOperations, useTransportStore} from "../store/transportStore";
import {TmsWaypointWithAddressType} from "../graphql/queries/getTmsPublishedTaskByDriverAccountId";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {formatTimestampByLanguage} from "../../../utils/dateTimeUtils";
import {useTranslation} from "react-i18next";
import TransportInfoItem from "../components/TransportInfoItem";
import TransportPhotoUploadCard from "../components/TransportPhotoUploadCard";
import TransportPhotoViewer from "../components/TransportPhotoViewer";
import transportPhotoService, {TransportPhotoType, TransportPositionType, uploadTransportPhotosWithTimeout} from "../services/TransportPhotoService";
import {TmsPath, TmsPathPre} from "../../../enums/TmsNavigatePathEnum";
import { TmsOperationSourceEnum } from "../../../enums/TmsEnum";
import { addWatermarkToPhoto, type WatermarkGpsCache } from "../services/TransportWatermarkService";
import { useConfigEnable } from "../../../graphql/queries/commonConfigQueries";
import {
    GRAY_CONFIG_TMS_DRIVER_PHOTO_WATERMARK_DISABLED,
    GRAY_CONFIG_TMS_DRIVER_WATERMARK_ANDROID_NATIVE_LOCATION_DISABLED,
    GRAY_CONFIG_TMS_DRIVER_WATERMARK_IOS_NATIVE_LOCATION_DISABLED,
} from "../../../constants/grayConfigKeys";

interface TransportArrivingPageProps {}

const TransportArrivingPage: React.FC<TransportArrivingPageProps> = () => {
    const navigate = useNavigate();
    const { taskWayPointId } = useParams<{
        taskWayPointId: string;
    }>();
    const { i18n } = useTranslation();
    const apolloClient = useApolloClient();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [waypointData, setWaypointData] = useState<TmsWaypointWithAddressType>();
    const { tmsTaskWaypoint, loading, refreshTmsTaskWaypointList } = useTmsTaskWaypoint(taskWayPointId || '');
    const { isEnabled: isWatermarkDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_PHOTO_WATERMARK_DISABLED);
    const { isEnabled: isAndroidNativeLocationDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_WATERMARK_ANDROID_NATIVE_LOCATION_DISABLED);
    const { isEnabled: isIosNativeLocationDisabled } = useConfigEnable(GRAY_CONFIG_TMS_DRIVER_WATERMARK_IOS_NATIVE_LOCATION_DISABLED);
    const watermarkGpsRef = useRef<WatermarkGpsCache | null>(null);
    const tmsTaskWaypointList = useTransportStore(state => state.tmsTaskWaypointList);
    const { updateTmsTaskWaypointToArrived } = useTmsTaskOperations();
    const [photos, setPhotos] = useState<Record<TransportPositionType, TransportPhotoType | undefined>>({} as Record<TransportPositionType, TransportPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);

    // 获取下一个未完成的节点
    const nextWaypoint = React.useMemo(() => {
        if (!waypointData || !tmsTaskWaypointList || tmsTaskWaypointList.length === 0) {
            return null;
        }
        // 按 sequence 排序找到当前节点
        const sortedWaypoints = [...tmsTaskWaypointList].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const currentIndex = sortedWaypoints.findIndex(
            wp => wp.taskWayPointId === waypointData.taskWayPointId
        );
        // 从当前节点后面找到第一个未完成的节点
        if (currentIndex >= 0 && currentIndex < sortedWaypoints.length - 1) {
            for (let i = currentIndex + 1; i < sortedWaypoints.length; i++) {
                const wp = sortedWaypoints[i];
                if (wp.status !== 'DONE') {
                    return wp;
                }
            }
        }
        return null;
    }, [waypointData, tmsTaskWaypointList]);

    useEffect(() => {
        if (tmsTaskWaypoint) {
            setWaypointData(tmsTaskWaypoint);
        }
    }, [tmsTaskWaypoint]);

    // 拍照或从相册选择
    const takePhoto = async (type: TransportPositionType, file: File) => {
        try {
            setIsLoading(true);
            let watermarkedFile = file;
            if (!isWatermarkDisabled) {
                const result = await addWatermarkToPhoto(file, {
                    ianaTimeZone: waypointData?.waypointTimezoneLocation,
                    apolloClient,
                    cachedGps: watermarkGpsRef.current,
                    language: i18n.language,
                    androidNativeLocationDisabled: isAndroidNativeLocationDisabled,
                    iosNativeLocationDisabled: isIosNativeLocationDisabled,
                });
                watermarkedFile = result.file;
                if (result.gpsCache) {
                    watermarkGpsRef.current = result.gpsCache;
                }
            }
            await transportPhotoService.handlePhotoNotUpload(
                type,
                watermarkedFile,
                (updateFn: (prev: Record<TransportPositionType, TransportPhotoType | undefined>) => Record<TransportPositionType, TransportPhotoType | undefined>) => {
                    setPhotos((prev) => updateFn(prev));
                }
            );
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: TransportPositionType) => {
        Dialog.confirm({
            content: t('pages.tms.transporting.arriving.deleteContent'),
            confirmText: t('pages.tms.transporting.arriving.deleteConfirm'),
            cancelText: t('pages.tms.transporting.arriving.deleteCancel'),
            onConfirm: () => {
                transportPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    // 始发站（waypointType=1）到达不需要封签照片，与 PDA/PC 逻辑对齐
    const isOrigin = waypointData?.waypointType === 1;

    // 检查照片数量：到达凭证至少2张；非始发站还需至少1张封签照片
    const hasEnoughPhotos = () => {
        const photoCount = [
            photos.signFor?.url,
            photos.warehousePhoto?.url,
            photos.doorNumber?.url
        ].filter(Boolean).length;
        if (photoCount < 2) return false;
        if (!isOrigin) {
            const sealPhotoCount = [
                photos.sealPhoto1?.url,
                photos.sealPhoto2?.url,
                photos.sealPhoto3?.url
            ].filter(Boolean).length;
            if (sealPhotoCount < 1) return false;
        }
        return true;
    };

    // 标记到达
    const handleMarkArrived = async () => {
        if (!waypointData || !taskWayPointId) {
            Toast.show({
                content: t('pages.tms.transporting.noWaypointInfo'),
                position: 'bottom',
            });
            return;
        }

        if (!hasEnoughPhotos()) {
            Toast.show({
                content: t('pages.tms.transporting.arriving.photoRequired'),
                position: 'bottom',
            });
            return;
        }

        setIsLoading(true);
        try {
            const currentPhotos = {...photos};

            // 分离到达凭证照片与封签照片，与 PDA 对齐：封签照片走独立字段 arrivalSealPhotosJson
            const isValidPhoto = (item: TransportPhotoType | undefined): item is TransportPhotoType =>
                item?.objectKey !== undefined && item?.url !== undefined;

            const arrivalPhotoItems = [
                photos.signFor,
                photos.warehousePhoto,
                photos.doorNumber,
            ].filter(isValidPhoto);

            const sealPhotoItems = isOrigin ? [] : [
                photos.sealPhoto1,
                photos.sealPhoto2,
                photos.sealPhoto3,
            ].filter(isValidPhoto);

            // 乐观提交：使用当前 objectKey（可能含占位符），字段分开传
            await updateTmsTaskWaypointToArrived({
                tmsTaskWayPointId: parseInt(taskWayPointId),
                taskId: waypointData.taskId || 0,
                photosS3KeysJson: arrivalPhotoItems.map(p => p.objectKey),
                ...(sealPhotoItems.length > 0 ? { arrivalSealPhotosJson: sealPhotoItems.map(p => p.objectKey) } : {}),
            }, true);
            setPhotos(currentPhotos);

            // 上传到达凭证照片
            const arrivalPhotoKeys = await uploadTransportPhotosWithTimeout(arrivalPhotoItems);

            // 上传封签照片（始发站跳过），复用同一上传工具，不重复显示 Toast
            const sealPhotoKeys = sealPhotoItems.length > 0
                ? await uploadTransportPhotosWithTimeout(sealPhotoItems, 5000, false)
                : [];

            // 正式提交，封签照片走 arrivalSealPhotosJson
            const success = await updateTmsTaskWaypointToArrived({
                tmsTaskWayPointId: parseInt(taskWayPointId),
                taskId: waypointData.taskId || 0,
                photosS3KeysJson: arrivalPhotoKeys,
                ...(sealPhotoKeys.length > 0 ? { arrivalSealPhotosJson: sealPhotoKeys } : {}),
                operationSource: TmsOperationSourceEnum.APP,
            }, false);

            if (success) {
                Toast.show({
                    content: t('pages.tms.transporting.arrivedSuccess'),
                    position: 'bottom',
                });
                // 刷新列表数据
                await refreshTmsTaskWaypointList();

                // 如果是终点（waypointType=3），返回列表页面
                if (waypointData.waypointType === 3) {
                    navigate(TmsPath.TMS_TRANSPORTING);
                } else {
                    // 返回上一页
                    navigate(-1);
                }
            } else {
                Toast.show({
                    content: t('pages.tms.transporting.arrivedFailed'),
                    position: 'bottom',
                });
            }
        } catch (error: any) {
            console.error('Failed to mark arrived:', error);
            if (error.networkError) {
                // 网络错误处理
                Toast.show({
                    content: t('pages.tms.transporting.arrivedFailed'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.tms.transporting.arrivedFailed'),
                    position: 'bottom',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 获取站点编号显示文本
    const getSiteCodeText = (): string => {
        const isStartPoint = waypointData?.waypointType === 1;
        const isEndPoint = waypointData?.waypointType === 3;
        if (isStartPoint) {
            return t('pages.tms.start');
        }
        if (isEndPoint) {
            return t('pages.tms.end');
        }
        return waypointData?.sequence?.toString() || '-';
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.tms.transporting.arriving.title')}
                    </NavBar>
                </div>
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={3} animated />
            </div>
        );
    }

    if (!waypointData) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.tms.transporting.arriving.title')}
                    </NavBar>
                </div>
                <EmptyState description={t('pages.tms.noTransportInfo')}/>
            </div>
        );
    }

    const address = buildAddressFromAppointment({
        addressLine1: waypointData.addressLine1 ?? undefined,
        addressLine2: waypointData.addressLine2 ?? undefined,
        city: waypointData.city ?? undefined,
        state: waypointData.state ?? undefined,
        zipcode: waypointData.zipcode ?? undefined,
        country: waypointData.country ?? undefined
    });

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>
                    {waypointData.wayPointName || t('pages.tms.transporting.arriving.title')}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 途经点信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.tms.transporting.arriving.information')}</h2>
                    <div className="mb-1">
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.name')} value={waypointData.wayPointName || '-'} />
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.sequence')} value={getSiteCodeText()} />
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.contact')} value={waypointData.contactName || '-'} />
                        <TransportInfoItem 
                            label={t('pages.tms.transporting.arriving.phone')} 
                            value={`${waypointData.contactPhone || ''}${waypointData.ext ? `-${waypointData.ext}` : ''}`} 
                        />
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.address')} value={address} />
                        {waypointData.planArrivalTime && (
                            <TransportInfoItem
                                label={t('pages.tms.transporting.planArrivalTime')}
                                value={formatTimestampByLanguage(waypointData.planArrivalTime, i18n.language) + (waypointData.planArrivalTimezone ? ` (${waypointData.planArrivalTimezone})` : '')}
                            />
                        )}
                        {waypointData.planDepartureTime && (
                            <TransportInfoItem
                                label={t('pages.tms.transporting.planDepartureTime')}
                                value={formatTimestampByLanguage(waypointData.planDepartureTime, i18n.language) + (waypointData.planDepartureTimezone ? ` (${waypointData.planDepartureTimezone})` : '')}
                            />
                        )}
                    </div>
                </div>

                {/*上传图片*/}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                <h2 className="text-base font-semibold mb-1">{t('pages.tms.transporting.arriving.photos')}</h2>
                <p className="text-sm text-gray-600 mb-1">
                    <span>{t('pages.tms.transporting.arriving.photoDesc')}</span>
                    <span className={`text-green-600 text-base`}>{t('pages.tms.transporting.arriving.photoDesc1')}</span>
                    {t('pages.tms.transporting.arriving.punctuation')}
                </p>
                <div className="grid grid-cols-3 gap-2 my-4">
                    <TransportPhotoUploadCard
                        photoUrl={photos.warehousePhoto?.url}
                        type="warehousePhoto"
                        description={``}
                        onTakePhoto={takePhoto}
                        onDelete={deletePhoto}
                        onView={handleViewPhoto}
                        cameraOnly
                    />
                    <TransportPhotoUploadCard
                        photoUrl={photos.doorNumber?.url}
                        type="doorNumber"
                        description={``}
                        onTakePhoto={takePhoto}
                        onDelete={deletePhoto}
                        onView={handleViewPhoto}
                        cameraOnly
                    />
                    <TransportPhotoUploadCard
                        photoUrl={photos.signFor?.url}
                        type="signFor"
                        description={``}
                        onTakePhoto={takePhoto}
                        onDelete={deletePhoto}
                        onView={handleViewPhoto}
                        cameraOnly
                    />

                </div>
                <TransportPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
                </div>

                {/*封签照片：始发站到达不需要，与 PDA/PC 对齐*/}
                {!isOrigin && <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.tms.transporting.arriving.sealPhotos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        <span>{t('pages.tms.transporting.arriving.sealPhotoDesc')}</span>
                        <span className="text-green-600 text-base">{t('pages.tms.transporting.arriving.sealPhotoCount')}</span>
                        <span>{t('pages.tms.transporting.arriving.sealPhotoDesc2')}</span>
                        <span className="text-green-600 text-base">{t('pages.tms.transporting.arriving.sealPhotoRequirement')}</span>
                        <span>{t('pages.tms.transporting.arriving.sealPhotoDesc3')}</span>
                    </p>
                    <div className="grid grid-cols-3 gap-2 my-4">
                        <TransportPhotoUploadCard
                            photoUrl={photos.sealPhoto1?.url}
                            type="sealPhoto1"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                            cameraOnly
                        />
                        <TransportPhotoUploadCard
                            photoUrl={photos.sealPhoto2?.url}
                            type="sealPhoto2"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                            cameraOnly
                        />
                        <TransportPhotoUploadCard
                            photoUrl={photos.sealPhoto3?.url}
                            type="sealPhoto3"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                            cameraOnly
                        />
                    </div>
                </div>}
            </div>

            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={!hasEnoughPhotos() || isLoading }
                    onClick={handleMarkArrived}
                >
                    {t('pages.tms.transporting.arrivedFinished')}
                </Button>
            </div>
        </div>
    );
};

export default TransportArrivingPage;
